import { StyleSheet, View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { Palette } from '@/constants/theme';
import { useAppTheme } from '@/theme/app-theme';

// Light theme keeps the two-tone navy + brand blue. In dark theme the navy
// "opn" disappears against the dark background, so both use a brighter, clearly
// visible blue.
/**
 * Spacing inside the mark, as ratios of `size` rather than points.
 *
 * -1.5pt and 3pt were fixed values sitting between glyphs that scale, so the
 * mark's proportions changed with the OS text setting: reducing the setting
 * opened a gap between "opn" and "cue", raising it closed one. Same class of
 * defect as a fixed-point nudge beside scaling text — the wordmark is one
 * shape and every distance inside it has to scale with the shape.
 *
 * -1.5/24 and 3/24 reproduce today's look exactly at the default size 24.
 */
const LETTER_SPACING_RATIO = -0.0625;
const DOT_GAP_RATIO = 0.125;

const LIGHT_LEAD = '#092D5B';
const LIGHT_BLUE = '#2468D4';
const DARK_LEAD = '#CBDDFA';
const DARK_BLUE = '#6EA8FF';

/**
 * opncue. wordmark:
 * "opn" in the lead color, "cue" in brand blue, with a blue pulse dot.
 */
export function Logo({ size = 30 }: { size?: number }) {
  const { isDark } = useAppTheme();
  const lead = isDark ? DARK_LEAD : LIGHT_LEAD;
  const blue = isDark ? DARK_BLUE : LIGHT_BLUE;

  return (
    <View style={styles.row}>
      <ThemedText
        style={[
          styles.word,
          {
            fontSize: size,
            lineHeight: size * 1.15,
            letterSpacing: size * LETTER_SPACING_RATIO,
            color: lead,
          },
        ]}>
        opn
      </ThemedText>

      <ThemedText
        style={[
          styles.word,
          {
            fontSize: size,
            lineHeight: size * 1.15,
            letterSpacing: size * LETTER_SPACING_RATIO,
            color: blue,
          },
        ]}>
        cue
      </ThemedText>

      <View
        style={[
          styles.dot,
          {
            backgroundColor: blue,
            width: Math.max(6, size * 0.22),
            height: Math.max(6, size * 0.22),
            borderRadius: Math.max(3, size * 0.11),
            marginLeft: size * DOT_GAP_RATIO,
            marginTop: size * 0.27,
          },
        ]}
      />
    </View>
  );
}

/** Square app mark for splash screens and headers. */
export function LogoMark({ size = 64 }: { size?: number }) {
  return (
    <View
      style={[
        styles.mark,
        {
          width: size,
          height: size,
          borderRadius: size * 0.28,
          backgroundColor: Palette.blue,
        },
      ]}>
      <View
        style={[
          styles.ring,
          {
            width: size * 0.46,
            height: size * 0.46,
            borderRadius: size * 0.23,
          },
        ]}
      />

      <View
        style={[
          styles.core,
          {
            width: size * 0.16,
            height: size * 0.16,
            borderRadius: size * 0.08,
          },
        ]}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
  },

  word: {
    fontWeight: '500',
    // letterSpacing is set per-instance from `size`, not here. A fixed -1.5pt
    // is a constant tightening applied to glyphs that change size with the OS
    // text setting, so the mark reads tight at a small setting and loose at a
    // large one — reported as the space between "opn" and "cue" opening and
    // closing as the slider moves. A ratio holds the proportion.
  },

  dot: {
    // marginLeft is set per-instance from `size`, for the reason in `word`.
  },

  mark: {
    alignItems: 'center',
    justifyContent: 'center',
  },

  ring: {
    position: 'absolute',
    borderWidth: 3,
    borderColor: 'rgba(255,255,255,0.65)',
  },

  core: {
    backgroundColor: '#FFFFFF',
  },
});